import { useMemo, useRef } from 'react';
import { isEmpty } from 'lodash';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { useExposure } from '../dbtStrategy/useExposure';
import { useGroup } from '../dbtStrategy/useGroup';
import { useMacro } from '../dbtStrategy/useMacro';
import { useMetric } from '../dbtStrategy/useMetric';
import { useModel } from '../dbtStrategy/useModel';
import { useSavedQuery } from '../dbtStrategy/useSavedQuery';
import { useSeed } from '../dbtStrategy/useSeed';
import { useSemanticModel } from '../dbtStrategy/useSemanticModel';
import { useSnapshot } from '../dbtStrategy/useSnapshot';
import { useSource } from '../dbtStrategy/useSource';
import { useTest } from '../dbtStrategy/useTest';
import { RequestedExposureAppliedStateNode } from './exposure';
import { RequestedGroupDefinitionNode } from './group';
import { RequestedMacroDefinitionNode } from './macro';
import { RequestedMetricDefinitionNode } from './metric';
import { RequestedModelAppliedNode } from './model';
import { RequestedSeedAppliedNode, RequestedSeedDefinitionNode } from './seed';
import { RequestedSemanticModelDefinitionNode } from './semanticModel';
import {
  RequestedSnapshotAppliedNode,
  RequestedSnapshotDefinitionNode,
} from './snapshot';
import { RequestedSourceAppliedNode, RequestedSourceDefinitionNode } from './source';
import { RequestedTestAppliedNode, RequestedTestDefinitionNode } from './test';

export type ResourceAppliedNode = RequestedExposureAppliedStateNode &
  RequestedModelAppliedNode &
  RequestedSeedAppliedNode &
  RequestedSnapshotAppliedNode &
  RequestedSourceAppliedNode &
  RequestedTestAppliedNode;

export type ResourceDefinitionNode = RequestedGroupDefinitionNode &
  RequestedMacroDefinitionNode &
  RequestedMetricDefinitionNode &
  RequestedSeedDefinitionNode &
  RequestedSnapshotDefinitionNode &
  RequestedSourceDefinitionNode &
  RequestedTestDefinitionNode &
  RequestedSemanticModelDefinitionNode;

export type GenericResourceNode = Partial<
  ResourceAppliedNode & ResourceDefinitionNode
> & {
  resourceType?: string;
};

export const RESOURCES_WITH_EXECUTION_INFO = [
  'ModelAppliedStateNode',
  'SeedAppliedStateNode',
  'SnapshotAppliedStateNode',
] as const;

export const getAppliedModel = (
  resource: GenericResourceNode | undefined | null,
): RequestedModelAppliedNode | null => {
  if (resource?.__typename !== 'ModelAppliedStateNode') {
    return null;
  }
  return resource as RequestedModelAppliedNode;
};

export const getAppliedSeed = (
  resource: GenericResourceNode | undefined | null,
): RequestedSeedAppliedNode | null => {
  if (resource?.__typename !== 'SeedAppliedStateNode') {
    return null;
  }
  return resource as RequestedSeedAppliedNode;
};

export const getAppliedExposure = (
  resource: GenericResourceNode | undefined | null,
): RequestedExposureAppliedStateNode | null => {
  if (resource?.__typename !== 'ExposureAppliedStateNode') {
    return null;
  }
  return resource as RequestedExposureAppliedStateNode;
};

export const getAppliedSnapshot = (
  resource: GenericResourceNode | undefined | null,
): RequestedSnapshotAppliedNode | null => {
  if (resource?.__typename !== 'SnapshotAppliedStateNode') {
    return null;
  }
  return resource as RequestedSnapshotAppliedNode;
};

export const getAppliedSource = (
  resource: GenericResourceNode | undefined | null,
): RequestedSourceAppliedNode | null => {
  if (resource?.__typename !== 'SourceAppliedStateNode') {
    return null;
  }
  return resource as RequestedSourceAppliedNode;
};

export const getAppliedTest = (
  resource: GenericResourceNode | undefined | null,
): RequestedTestAppliedNode | null => {
  if (resource?.__typename !== 'TestAppliedStateNode') {
    return null;
  }
  return resource as RequestedTestAppliedNode;
};

export const getDefinitionGroup = (
  resource: GenericResourceNode | undefined | null,
): RequestedGroupDefinitionNode | null => {
  if (resource?.__typename !== 'GroupNode') {
    return null;
  }
  return resource as RequestedGroupDefinitionNode;
};

export const getDefinitionMacro = (
  resource: GenericResourceNode | undefined | null,
): RequestedMacroDefinitionNode | null => {
  if (resource?.__typename !== 'MacroDefinitionNode') {
    return null;
  }
  return resource as RequestedMacroDefinitionNode;
};

export const getDefinitionMetric = (
  resource: GenericResourceNode | undefined | null,
): RequestedMetricDefinitionNode | null => {
  if (resource?.__typename !== 'MetricDefinitionNode') {
    return null;
  }
  return resource as RequestedMetricDefinitionNode;
};

export const getDefinitionSemanticModel = (
  resource: GenericResourceNode | undefined | null,
): RequestedSemanticModelDefinitionNode | null => {
  if (resource?.__typename !== 'SemanticModelDefinitionNode') {
    return null;
  }
  return resource as any as RequestedSemanticModelDefinitionNode;
};

export const getResourceWithExecutionInfo = (
  resource: GenericResourceNode | undefined | null,
):
  | RequestedModelAppliedNode
  | RequestedSeedAppliedNode
  | RequestedSnapshotAppliedNode
  | null => {
  let res = null;
  res = getAppliedModel(resource);
  if (res) return res;

  res = getAppliedSeed(resource);
  if (res) return res;

  res = getAppliedSnapshot(resource);
  if (res) return res;
  return null;
};

export const useGenericResource = (
  _uniqueId: ParsedUniqueId | undefined | null,
  {
    pause,
    environmentId: initialEnvironmentId,
  }: { pause?: boolean; environmentId?: number } = {},
): DbtResponse<GenericResourceNode> => {
  const { environmentId: inheritedEnvironmentId } = useDbtCloudProject();
  const environmentId = initialEnvironmentId ?? inheritedEnvironmentId;
  const uniqueId = pause ? undefined : (_uniqueId ?? undefined);

  const exposureQuery = useExposure(uniqueId, environmentId);
  const modelQuery = useModel(uniqueId, environmentId);
  const seedQuery = useSeed(uniqueId, environmentId);
  const snapshotQuery = useSnapshot(uniqueId, environmentId);
  const sourceQuery = useSource(uniqueId, environmentId);
  const testQuery = useTest(uniqueId, environmentId);

  const macroQuery = useMacro(uniqueId, environmentId);
  const metricQuery = useMetric(uniqueId, environmentId);
  const semanticModelQuery = useSemanticModel(uniqueId, environmentId);
  const savedQueryQuery = useSavedQuery(uniqueId, environmentId);
  const groupQuery = useGroup(uniqueId, environmentId);

  const appliedQueries = useMemo(
    () => [exposureQuery, modelQuery, seedQuery, snapshotQuery, sourceQuery, testQuery],
    [exposureQuery, modelQuery, seedQuery, snapshotQuery, sourceQuery, testQuery],
  );

  const definitionQueries = useMemo(
    () => [macroQuery, metricQuery, semanticModelQuery, savedQueryQuery, groupQuery],
    [macroQuery, metricQuery, semanticModelQuery, savedQueryQuery, groupQuery],
  );
  const allQueriesRef = useRef([...appliedQueries, ...definitionQueries]);
  allQueriesRef.current = [...appliedQueries, ...definitionQueries];

  return useMemo(() => {
    const fetching = allQueriesRef.current.some((query) => query.fetching);
    const error = allQueriesRef.current.find((query) => query.error)?.error;

    if (fetching) {
      return { error: undefined, fetching: true, data: undefined };
    }

    if (error) {
      return { error, fetching, data: undefined };
    }

    if (!environmentId || allQueriesRef.current.every((query) => query.data == null)) {
      return { data: undefined, fetching: false, error: undefined };
    }
    const resourceFilter = (query: {
      data?: {
        applied?: { uniqueId?: string } | null;
        definition?: { uniqueId?: string } | null;
      } | null;
    }) => {
      if (!_uniqueId) return;
      return (
        query.data?.applied?.uniqueId === _uniqueId.raw ||
        query.data?.definition?.uniqueId === _uniqueId.raw
      );
    };
    const resource = Object.assign(
      {},
      ...appliedQueries.filter(resourceFilter).map((query) => query.data.applied),
      ...definitionQueries.filter(resourceFilter).map((query) => query.data.definition),
    ) as ResourceAppliedNode;
    return {
      data: (isEmpty(resource)
        ? resource
        : {
            resourceType: _uniqueId?.resourceType,
            ...resource,
          }) as GenericResourceNode,
      fetching: false,
      error: undefined,
    };
  }, [environmentId, _uniqueId, appliedQueries, definitionQueries]);
};

export type DbtResponse<TData, TError = any> = {
  error: TError | undefined;
  data: TData | undefined;
  fetching: boolean;
};

/**
 * codex-api returns fqns as (string | null)[] | null and we should fix that
 * to instead return string[] | null
 */
export function asFqn(fqn: (string | null)[] | null | undefined) {
  return (fqn ?? null) as any as string[] | null;
}
